import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../components/ui/card.jsx';
import { Skeleton } from "../../components/ui/skeleton.jsx";
import { DateTime } from 'luxon';
import axios from "../../lib/axios.js";
import { Mail, Clock, User, Phone } from 'lucide-react';

function EmailsPage() {
  const { id } = useParams();
  const [emailData, setEmailData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  // Create the HTML content for the iframe
  const createEmailHTML = ({html, text}) => `
    <!DOCTYPE html>
    <html>
      <head>
        <base target="_blank">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.5;
            color: #1a1a1a;
            margin: 0;
            padding: 16px;
            overflow-y: hidden;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          table {
            max-width: 100%;
            margin: 0 auto;
          }
          td {
            padding: 8px;
            word-break: break-word;
          }
          div {
            max-width: 100%;
          }
          * {
            max-width: 100%;
          }
          @media (max-width: 640px) {
            table {
              width: 100% !important;
            }
            td {
              display: block;
              width: 100% !important;
            }
          }
        </style>
      </head>
      <body>
        ${!html ? text.replace(/\n/g, '<br>') : html}
        <script>
          // Send height updates to parent
          function updateHeight() {
            const height = document.body.scrollHeight;
            window.parent.postMessage({ type: 'resize', height: height }, '*');
          }
          // Call initially and on any content changes
          updateHeight();
          window.addEventListener('resize', updateHeight);
          new MutationObserver(updateHeight).observe(document.body, {
            childList: true,
            subtree: true
          });
        </script>
      </body>
    </html>
  `;

  // Handle iframe height updates from postMessage
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'resize' && iframeRef.current) {
        // Debounce the resize
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = setTimeout(() => {
          iframeRef.current.style.height = `${event.data.height + 32}px`;
        }, 100);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Handle iframe content initialization
  useEffect(() => {
    if (iframeRef.current && (emailData?.html || emailData?.text)) {
      const iframe = iframeRef.current;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      
      // Write the content to the iframe
      iframeDocument.open();
      iframeDocument.write(createEmailHTML(emailData));
      iframeDocument.close();

      // Set initial height
      iframe.style.height = '500px'; // Default height while loading
    }
  }, [emailData]);

  // Fetch email data
  useEffect(() => {
    async function fetchEmailData() {
      try {
        const response = await axios.get(`/emails/${id}`);
        const { data } = response;
        setEmailData(data);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    }
    fetchEmailData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-full min-w-full flex flex-col">
        <Card className="w-full max-w-7xl mx-auto">
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-56" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-6 w-36" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Skeleton className="h-8 w-24" />
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  const { from_email: fromEmail, to_email: toEmail, received_at: receivedAt } = emailData;

  const timezone = emailData?.community?.time_zone || 'UTC';

  const emailRecievedAt = DateTime.fromISO(receivedAt).setZone(timezone);
  const currentYear = DateTime.now().setZone(timezone).year;
  const fmt = emailRecievedAt.year === currentYear
    ? 'MMM d, hh:mm a ZZZZ'        // e.g., “May 05, 09:00 AM”
    : 'MMM d, yyyy hh:mm a ZZZZ';  // e.g., “May 05, 2024 09:00 AM”

  const formattedDate = emailRecievedAt.toFormat(fmt);

  return (
    <div className="min-h-full min-w-full flex flex-col p-4">
      <Card className="w-full max-w-4xl mx-auto shadow-lg">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="text-2xl font-semibold text-gray-900">
            Email Details
          </CardTitle>
          <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-gray-600">
            {fromEmail && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="font-medium">From:</span> {fromEmail}
              </div>
            )}
            {toEmail && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="font-medium">To:</span> {toEmail}
              </div>
            )}
            {formattedDate && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="font-medium">Received:</span> {formattedDate}
              </div>
            )}

            {emailData.prospect && (
              <div className='mt-5 border-t grid grid-cols-1 gap-2'>
                <h1 className='font-bold mt-2 text-lg'>Prospect</h1>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {emailData.prospect.first_name} {emailData.prospect.last_name}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {emailData.prospect.phone_number}
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {emailData.prospect.email}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          <div className="bg-white rounded-b-lg">
            <iframe
              ref={iframeRef}
              className="w-full border-0"
              title="Email Content"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailsPage;